import { useState } from "react";
import { DataTable } from "../components/DataTable";
import type { DataTableColumn } from "../components/DataTable";

type Return = {
  id: string;
  orderId: string;
  customer: string;
  product: string;
  reason: string;
  requestDate: string;
  amount: number;
  status: "pending" | "approved" | "rejected" | "refunded";
};

const initialReturns: Return[] = [
  { id:"rt1", orderId:"ORD-2845", customer:"Priya Sharma", product:"Kundan Choker Set", reason:"Color different from photos", requestDate:"2025-04-26", amount:7999, status:"pending" },
  { id:"rt2", orderId:"ORD-2810", customer:"Anjali Singh", product:"Temple Gold Earrings", reason:"Received damaged product", requestDate:"2025-04-22", amount:4499, status:"approved" },
  { id:"rt3", orderId:"ORD-2790", customer:"Meena Patel", product:"Polki Bridal Necklace", reason:"Wrong item delivered", requestDate:"2025-04-18", amount:18999, status:"refunded" },
  { id:"rt4", orderId:"ORD-2760", customer:"Rekha Gupta", product:"Pearl Bangles Set", reason:"Changed mind", requestDate:"2025-04-10", amount:3299, status:"rejected" },
];

const statusStyle: Record<Return["status"], { bg: string; color: string }> = {
  pending:  { bg:"#fef3c7", color:"#92400e" },
  approved: { bg:"#dbeafe", color:"#1d4ed8" },
  rejected: { bg:"#fee2e2", color:"#991b1b" },
  refunded: { bg:"#dcfce7", color:"#166534" },
};

export function ReturnsPage() {
  const [returns, setReturns] = useState<Return[]>(initialReturns);
  const [filterStatus, setFilterStatus] = useState<"all" | Return["status"]>("all");

  function updateStatus(id: string, status: Return["status"]) {
    setReturns((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  }

  const filteredRows =
    filterStatus === "all"
      ? (returns as unknown as Record<string, unknown>[])
      : (returns.filter((r) => r.status === filterStatus) as unknown as Record<string, unknown>[]);

  const columns: DataTableColumn<Record<string, unknown>>[] = [
    {
      key: "orderId",
      label: "Order ID",
      sortable: true,
      render: (row) => <code className="ret-code">{String(row.orderId)}</code>,
    },
    {
      key: "customer",
      label: "Customer",
      sortable: true,
      render: (row) => (
        <div>
          <div className="ret-cust">{String(row.customer)}</div>
          <div className="ret-date">📅 {String(row.requestDate)}</div>
        </div>
      ),
    },
    {
      key: "product",
      label: "Product",
      sortable: true,
      render: (row) => (
        <div>
          <div className="ret-prod">💍 {String(row.product)}</div>
          <div className="ret-reason">{String(row.reason)}</div>
        </div>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      render: (row) => <span className="ret-amount">₹{Number(row.amount).toLocaleString()}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => {
        const s = row.status as Return["status"];
        return (
          <span className="pg-badge" style={{ background: statusStyle[s].bg, color: statusStyle[s].color }}>
            {s}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      className: "text-end",
      render: (row) => {
        const id = String(row.id);
        const s = row.status as Return["status"];
        return (
          <div className="pg-actions">
            {s === "pending" && (
              <>
                <button type="button" className="pg-btn-approve" onClick={() => updateStatus(id, "approved")}>✓ Approve</button>
                <button type="button" className="pg-btn-reject"  onClick={() => updateStatus(id, "rejected")}>✕ Reject</button>
              </>
            )}
            {s === "approved" && (
              <button type="button" className="pg-btn-resolve" onClick={() => updateStatus(id, "refunded")}>💸 Refund</button>
            )}
            {(s === "rejected" || s === "refunded") && (
              <span className="pg-btn-neutral" style={{ border: "none", cursor: "default" }}>—</span>
            )}
          </div>
        );
      },
    },
  ];

  const filterBtns: { key: "all" | Return["status"]; label: string }[] = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "refunded", label: "Refunded" },
  ];

  const totalPending = returns.filter((r) => r.status === "pending").reduce((a, r) => a + r.amount, 0);

  return (
    <div className="d-grid gap-4">
      <div className="pg-stats-grid">
        {[
          { label: "Pending Returns", val: returns.filter((r) => r.status === "pending").length, bg: "#fef3c7", color: "#92400e" },
          { label: "Pending Amount", val: `₹${totalPending.toLocaleString()}`, bg: "#fee2e2", color: "#991b1b" },
          { label: "Refunded", val: returns.filter((r) => r.status === "refunded").length, bg: "#dcfce7", color: "#166534" },
          { label: "Total Requests", val: returns.length, bg: "#ede9fe", color: "#5b21b6" },
        ].map((s) => (
          <div key={s.label} className="pg-stat-card" style={{ background: s.bg }}>
            <div className="pg-stat-label" style={{ color: s.color }}>{s.label}</div>
            <div className="pg-stat-value-sm" style={{ color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      <div className="pg-filter-bar">
        {filterBtns.map((f) => (
          <button key={f.key} type="button" onClick={() => setFilterStatus(f.key)}
            className={`pg-filter-btn ${filterStatus === f.key ? "active" : ""}`}>
            {f.label}
          </button>
        ))}
      </div>

      <DataTable
        title="Return Requests"
        rows={filteredRows}
        columns={columns}
        rowKey={(row) => String(row.id)}
        searchPlaceholder="Search by customer, product, order..."
        exportFileName="returns"
        pageSize={6}
      />
    </div>
  );
}
